import { LogRocketSdk } from '@mgmresorts/client-log-rocket';

import { actually } from '../utils/logging';
import { isSSR } from '../utils/url';

type StringNumberBoolean = string | number | boolean;

type LogRocketTrackContext = {
  [key: string]: StringNumberBoolean;
};

type LogRocketTrackEventProperties = {
  [key: string]: string | number | boolean | string[] | number[] | boolean[] | undefined;
};

type LogRocketIdentify = Parameters<typeof LogRocketSdk.identify>;

export const canCallLogRocket = !isSSR() && !!process.env.REACT_APP_LOGROCKET_APP_ID;

const showMissingAppIdMessage = () => actually.info('LR: App ID not provided');

export const makeLogRocketCall = <T>(fn: () => T) => (canCallLogRocket ? fn() : showMissingAppIdMessage());

export const initializeLogRocket = () => {
  if (process.env.REACT_APP_LOGROCKET_APP_ID) {
    makeLogRocketCall(() =>
      LogRocketSdk.init({
        appId: process.env.REACT_APP_LOGROCKET_APP_ID ?? '',
        options: {
          debug: true,
          overrides: {
            release: process.env.BUILD_ID,
            dom: {
              inputSanitizer: true,
            },
          },
        },
      }),
    );
  }
};

export const createLogRocketIdentify = (uid: LogRocketIdentify[0], traits?: LogRocketIdentify[1]) =>
  makeLogRocketCall(() => LogRocketSdk.identify(uid, traits));

export const captureMessage = (message: string, tags?: StringNumberBoolean, extra?: LogRocketTrackContext) =>
  makeLogRocketCall(() =>
    LogRocketSdk.captureMessage(message, {
      extra,
      tags: tags ? { 'identity-exception-tag': tags } : {},
    }),
  );

export const captureException = (error: Error, tags?: StringNumberBoolean, extra?: LogRocketTrackContext) =>
  makeLogRocketCall(() =>
    LogRocketSdk.captureException(error, {
      extra,
      tags: tags ? { 'identity-exception-tag': tags } : {},
    }),
  );

export const captureEvent = (event: string, additionalInformation?: LogRocketTrackEventProperties) => {
  makeLogRocketCall(() => LogRocketSdk.captureEvent(`[Identity] ${event}`, additionalInformation));
};
